import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { CalendarItemSize, DragItemType, Order, Photographer } from '../../BookingsCalendarTypes';
import { ClickAwayListener, tooltipClasses, TooltipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { ContainerSizeAndPosition } from '../../state/useCalendarContainerWidth';
import {
    CalendarBounds,
    CalendarDragPosition,
    handleCalendarAutoScrollOnDrag
} from '../../state/scrollHandler';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { formatDate } from '../../../../../shared/LanguageService';
import {
    createGhostDragImage,
    createGhostResizeImage,
    resizeBySlotWidth
} from './timelineCalendarHelperFuncitons';

type NewBookingEventProps = {
    containerSizeAndPosition: ContainerSizeAndPosition;
    newBookingEvent: Order;
    isOnFirstRow: boolean;
    newBookingStartTime: Date;
    newBookingEndTime: Date;
    newBookingEventStartIndex: number;
    newBookingEventEndIndex: number;
    newBookingLenght: number;
    photographer: Photographer;
    isAdvancedMode: boolean;
    onPreventResize: (preventResize: boolean) => void;
    onResizing: (isResizing: boolean) => void;
};

const newBookingStyle = (newBookingEventStart, newBookingEventEnd, isOnFirstRow) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        position: 'absolute',
        gridColumnStart: `${newBookingEventStart}`,
        gridColumnEnd: `${newBookingEventEnd}`,
        backgroundColor: '#64B6F7',
        height: isOnFirstRow ? CalendarItemSize.firstRowLarge : CalendarItemSize.large,
        borderRadius: '2px',
        zIndex: 10,
        top: isOnFirstRow ? '0.32rem' : '0.22rem',
        left: 0,
        bottom: '1px',
        right: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        cursor: 'grab'
    };
};

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        display: 'flex',
        justifyContent: 'center',
        margin: 'auto',
        backgroundColor: '#64B6F7',
        maxWidth: 200,
        fontSize: theme.typography.pxToRem(10),
        padding: '8px',
        border: '1px solid #9E9E9E',
        color: 'black'
    }
}));

const NewBookingEventItem = React.forwardRef(
    (
        {
            containerSizeAndPosition,
            newBookingEvent,
            isOnFirstRow,
            newBookingStartTime,
            newBookingEndTime,
            newBookingEventStartIndex,
            newBookingEventEndIndex,
            isAdvancedMode,
            onPreventResize,
            onResizing
        }: NewBookingEventProps,
        scrollRef: React.MutableRefObject<HTMLElement>
    ) => {
        const [openTooltip, setOpenTooltip] = React.useState<boolean>(false);

        let initialPosRef: React.MutableRefObject<number> = React.useRef(0);
        let initialSizeRef: React.MutableRefObject<number> = React.useRef(0);
        let isResizingRef: React.MutableRefObject<boolean> = React.useRef(false);

        const drag: React.MutableRefObject<any> = React.useRef(null);
        const resizer: React.MutableRefObject<any> = React.useRef(null);

        let slotPixelWidth: number = 6;

        let formattedStartTime = newBookingStartTime
            ? formatDate(new Date(newBookingStartTime), 'kk' + ':' + 'mm')
            : null;
        let formattedEndTime = newBookingEndTime
            ? formatDate(new Date(newBookingEndTime), 'kk' + ':' + 'mm')
            : null;

        const handleTooltipClose = () => {
            setOpenTooltip(false);
        };

        const handleToggleTooltip = () => {
            setOpenTooltip((currentState) => !currentState);
        };

        const handleMouseDown = (e) => {
            e.currentTarget.style.cursor = 'grabbing';
        };

        const handleMouseUp = (e) => {
            e.currentTarget.style.cursor = 'grab';
        };

        const onDragStart = (e) => {
            e.dataTransfer.clearData(e.target.id);
            e.dataTransfer.setData('type', e.target.id);
            e.dataTransfer.effectAllowed = 'move';

            let type = e.dataTransfer.getData('type');

            if (type === DragItemType.Drag) {
                onResizing(false);
                let dragItem = drag.current;

                let height = dragItem.offsetHeight;
                let width = dragItem.offsetWidth;

                createGhostDragImage(e, height, width, dragItem);
            }

            if (type === DragItemType.Resize) {
                e.dataTransfer.setData('type', e.target.id);
                isResizingRef.current = true;

                let resizeItem = resizer.current;
                createGhostResizeImage(e, resizeItem);

                initialSizeRef.current = drag.current.offsetWidth;

                initialPosRef.current = e.clientX;
            }
        };

        const handleDrag = (e) => {
            e.target.style.zIndex = 1;
            e.target.style.opacity = 0.5;
            let STEP: number = 4;

            if (scrollRef.current) {
                let boundingClient: ClientRect | DOMRect =
                    scrollRef.current.getBoundingClientRect();

                let bounds: CalendarBounds = {
                    left: boundingClient.left,
                    right: containerSizeAndPosition.right
                };

                let position: CalendarDragPosition = {
                    left: e.clientX,
                    right: e.clientX + e.target.clientWidth
                };

                handleCalendarAutoScrollOnDrag(scrollRef.current, bounds, position, STEP);
            }
        };

        const onDragEnd = (e) => {
            onPreventResize(false);
            let type = e.target.id;

            initialSizeRef.current = 0;
            initialPosRef.current = 0;
            isResizingRef.current = false;

            if (type === DragItemType.Drag) {
                e.currentTarget.style.cursor = 'grab';
                e.target.style.opacity = 1;
                e.target.style.zIndex = 10;
            }
            if (type === DragItemType.Resize && e.dataTransfer.dropEffect === 'none') {
                onResizing(false);
                drag.current.style.cursor = 'grab';
                drag.current.style.opacity = 1;
                drag.current.style.zIndex = 10;
                e.target.style.zIndex = 200;
            }
            if (type === DragItemType.Resize && e.dataTransfer.dropEffect === 'move') {
                onResizing(false);
                drag.current.style.cursor = 'grab';
                drag.current.style.opacity = 1;
                drag.current.style.zIndex = 10;
                e.target.style.zIndex = 200;
            }
        };

        const handleResizingDrag = (e) => {
            let resizedWidth: number = initialSizeRef.current + e.clientX - initialPosRef.current;
            let dragItem: React.MutableRefObject<any> = drag.current;
            let fiveMinutesInPixels = 6;
            let minBookingTime: number = fiveMinutesInPixels;

            if (resizedWidth > minBookingTime) {
                onResizing(true);
                onPreventResize(false);
                e.target.style.zIndex = 1;
                drag.current.style.zIndex = 1;
                drag.current.style.opacity = 0.5;
                resizeBySlotWidth(resizedWidth, slotPixelWidth, dragItem);
            }
            if (resizedWidth <= minBookingTime) {
                onResizing(false);
                drag.current.style.opacity = 1;
                drag.current.style.zIndex = 10;
                onPreventResize(true);
            }

            if (scrollRef.current) {
                let STEP: number = 1;
                let boundingClient = scrollRef.current.getBoundingClientRect();

                let bounds: CalendarBounds = {
                    left: boundingClient.left,
                    right: containerSizeAndPosition.right
                };

                let position: CalendarDragPosition = {
                    left: e.pageX,
                    right: e.pageX + e.target.clientWidth
                };

                handleCalendarAutoScrollOnDrag(scrollRef.current, bounds, position, STEP);
            }
        };

        function handleDragover(e) {
            if (isResizingRef.current) {
                handleResizingDrag(e);
            }
        }

        React.useEffect(() => {
            document.addEventListener('dragover', handleDragover);

            return () => {
                document.removeEventListener('dragover', handleDragover);
            };
        }, []);

        return (
            <ClickAwayListener onClickAway={handleTooltipClose}>
                <Box>
                    <Box key={newBookingEvent.orderId}>
                        <StyledTooltip
                            PopperProps={{
                                disablePortal: true
                            }}
                            onClose={handleTooltipClose}
                            open={openTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            placement="right"
                            sx={{ opacity: '0.8' }}
                            title={
                                <Box>
                                    <Typography>
                                        {newBookingEvent.orderAddress.address},
                                        {newBookingEvent.orderAddress.city}
                                    </Typography>
                                    <Typography>
                                        {formattedStartTime} -{formattedEndTime}
                                    </Typography>
                                    <Typography>{newBookingEvent.packageProduct}</Typography>
                                </Box>
                            }
                        >
                            <Box
                                ref={drag}
                                id={'drag-item'}
                                key={newBookingEvent.orderId}
                                sx={newBookingStyle(
                                    newBookingEventStartIndex,
                                    newBookingEventEndIndex,
                                    isOnFirstRow
                                )}
                                onDoubleClick={handleToggleTooltip}
                                draggable
                                onDragStart={onDragStart}
                                onDrag={handleDrag}
                                onDragEnd={onDragEnd}
                                onMouseDown={handleMouseDown}
                                onMouseUp={handleMouseUp}
                            >
                                <Box sx={{ margin: '8px' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            fontFamily: 'Gilroy-Regular',
                                            fontWeight: 650
                                        }}
                                    >
                                        {newBookingEvent.orderAddress.address}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            fontFamily: 'Gilroy-Regular',
                                            fontWeight: 400
                                        }}
                                    >
                                        {formattedStartTime} - {formattedEndTime}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            fontFamily: 'Gilroy',
                                            fontWeight: 400
                                        }}
                                    >
                                        {newBookingEvent.packageProduct}
                                    </Typography>
                                </Box>

                                {isAdvancedMode ? (
                                    <>
                                        <Box
                                            ref={resizer}
                                            id={'resizer'}
                                            draggable
                                            onDragStart={onDragStart}
                                            onDragEnd={onDragEnd}
                                            sx={{
                                                position: 'absolute',
                                                right: 0,
                                                bottom: 0,
                                                width: '2px',
                                                height: isOnFirstRow ? '66px' : '67px',
                                                cursor: 'e-resize',
                                                zIndex: 200
                                            }}
                                        />
                                        <DragHandleIcon
                                            sx={{
                                                transform: 'rotate(90deg)',
                                                position: 'absolute',
                                                bottom: '20px',
                                                right: '1px',
                                                width: '12px',
                                                zIndex: 2,
                                                padding: 0
                                            }}
                                        />
                                    </>
                                ) : null}
                            </Box>
                        </StyledTooltip>
                    </Box>
                </Box>
            </ClickAwayListener>
        );
    }
);

export default NewBookingEventItem;
